/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  DataAxisProps,
  Dimension,
  Group,
  GroupAxisProps,
  TimeAxisProps
} from '../../UNSAFE_Axis/axis.types';
import { BarSeries } from '../../UNSAFE_BarChart/barChart.types';
import { DataAxisInfo } from '../../UNSAFE_Axis/dataAxisInfo';
import { getDataAxisProps } from '../../UNSAFE_Axis/utils/dataAxisUtil';
import { getDataExtent } from './dataUtils';
import { AxisPosition } from '../../UNSAFE_Axis/axis.types';
import { getAxesPosition } from '../../UNSAFE_Axis/utils/axisUtils';
import { getGroupAxisInfo, getGroupAxisProps } from '../../UNSAFE_Axis/groupAxis';
import { ChartItem, ChartSeries, Scale } from '../../UNSAFE_Chart/chart.types';
import { getStartAndEndTime, getTimeAxisInfo, getTimeAxisProps } from '../../UNSAFE_Axis/timeAxis';
import type { useTextDimensions } from '../../hooks/PRIVATE_useTextDimensions';
type GetTextDimensions = NonNullable<ReturnType<typeof useTextDimensions>['getTextDimensions']>;

const OUTER_GAP_WIDTH = 10;
const OUTER_GAP_HEIGHT = 8;
const MIN_SCROLL_SIZE = 12;
const SCROLL_GAP = 6;

/**
 * Returns the chart layout info.
 * @returns
 */
export function getLayoutInfo<K extends string | number, D extends ChartItem<K>>(
  series: BarSeries<K, D>[],
  groups: Group[],
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  xAxis: GroupAxisProps | TimeAxisProps | undefined,
  yAxis: DataAxisProps | undefined,
  availSpace: Dimension,
  hiddenSet: Set<number | string>,
  hideAndShowBehavior: 'none' | 'withRescale' | 'withoutRescale',
  isHoriz: boolean,
  isRtl: boolean,
  isStacked: boolean,
  axisFontSize: string,
  offset: number,
  getTextDimensions?: GetTextDimensions
) {
  const isTimeAxis = (xAxis as TimeAxisProps)?.timeAxisType != null;
  const { xAxisPosition, yAxisPosition } = getAxesPosition(yAxis, isHoriz, isRtl);
  let xAxisInfo;
  if (isTimeAxis) {
    const timeAxisRange = getStartAndEndTime(
      groups,
      series,
      getDataItem,
      (xAxis as TimeAxisProps).timeAxisType
    );
    xAxisInfo = getTimeAxisInfo(groups, xAxis as TimeAxisProps, offset, timeAxisRange);
  } else {
    xAxisInfo = getGroupAxisInfo(groups, xAxis as GroupAxisProps, offset);
  }

  const { dataMax, dataMin } = getDataExtent(
    series,
    getDataItem,
    xAxisInfo.startIndex,
    xAxisInfo.endIndex,
    isStacked,
    hiddenSet,
    hideAndShowBehavior,
    yAxis
  );

  const yAxisInfo = new DataAxisInfo({ ...yAxis, dataMax, dataMin });
  const { axisDim: yDim } = getDataAxisProps(
    yAxisPosition,
    cloneDimension(availSpace),
    isRtl,
    yAxis,
    yAxisInfo,
    axisFontSize,
    getTextDimensions
  );

  const isXAxisHoriz = xAxisPosition === 'bottom';

  // get updated available space for x axis.
  const { xSpace: xAvailSpace } = getSpaces(
    availSpace,
    xAxisPosition,
    yAxisPosition,
    isXAxisHoriz ? availSpace.height : availSpace.width,
    yDim
  );

  const {
    axisDim: xMaxDim,
    scale: xScale,
    groupWidth,
    ...xProps
  } = isTimeAxis
    ? getTimeAxisProps(
        groups,
        xAxisPosition,
        xAvailSpace,
        xAxisInfo.viewportMin,
        xAxisInfo.viewportMax,
        // @ts-ignore
        xAxisInfo.averageInterval,
        isRtl,
        xAxis as TimeAxisProps,
        axisFontSize,
        getTextDimensions
      )
    : getGroupAxisProps(
        groups,
        xAxisPosition,
        xAvailSpace,
        isRtl,
        xAxisInfo.viewportMin,
        xAxisInfo.viewportMax,
        xAxisInfo.startIndex,
        xAxisInfo.endIndex,
        isHoriz,
        xAxis as GroupAxisProps,
        axisFontSize,
        getTextDimensions
      );

  // recalculate y axis props after the x axis.
  const { ySpace: newYSpace } = getSpaces(
    availSpace,
    xAxisPosition,
    yAxisPosition,
    xMaxDim!,
    isXAxisHoriz ? availSpace.width : availSpace.height
  );

  const newYProps = getDataAxisProps(
    yAxisPosition,
    newYSpace,
    isRtl,
    yAxis,
    yAxisInfo,
    axisFontSize,
    getTextDimensions
  );

  const { axisDim: yMaxDim, scale: yScale, ...yProps } = newYProps;

  const { plotAreaSpace } = getSpaces(availSpace, xAxisPosition, yAxisPosition, xMaxDim!, yMaxDim);

  return {
    xViewportExtent: { viewportMin: xAxisInfo.viewportMin, viewportMax: xAxisInfo.viewportMax },
    xIndexExtent: { startIndex: xAxisInfo.startIndex, endIndex: xAxisInfo.endIndex },
    yViewportExtent: yAxisInfo.getAxisViewport(),
    yAxisExtent: yAxisInfo.getAxisExtent(),
    xAxisExtent: { min: xAxisInfo.min, max: xAxisInfo.max },
    xProps,
    xScale,
    xAxisPosition,
    groupWidth,
    yProps,
    yScale,
    yMaxDim,
    yAxisPosition,
    yMajorTicks: yAxisInfo.getTicks(),
    yMinorTicks: yAxisInfo.getMinorTicks(),
    plotAreaSpace
  };
}

/**
 * Returns the available space for x axis and y axis.
 * @param availSpace The available space of the chart.
 * @param xAxisPosition The position of x axis.
 * @param yAxisPosition The position of y axis.
 * @param xMaxDim The max dimension of x axis.
 * @param yMaxDim The max dimension of y axis.
 * @returns
 */
export function getSpaces(
  availSpace: Dimension,
  xAxisPosition: AxisPosition,
  yAxisPosition: AxisPosition,
  xMaxDim: number,
  yMaxDim: number
) {
  const isXHoriz = xAxisPosition === 'bottom' || xAxisPosition === 'top';
  const isYLeft = yAxisPosition === 'left';

  let xSpace: Dimension;
  let ySpace: Dimension;
  let plotAreaSpace: Dimension;

  if (isXHoriz) {
    xSpace = getHorizSpace(
      availSpace,
      xAxisPosition,
      isYLeft ? yMaxDim : 0,
      !isYLeft ? yMaxDim : 0,
      xMaxDim
    );
    ySpace = getVertSpace(
      availSpace,
      yAxisPosition as 'left' | 'right',
      xAxisPosition === 'bottom' ? xMaxDim : 0,
      xAxisPosition === 'top' ? xMaxDim : 0,
      yMaxDim
    );
    plotAreaSpace = {
      x: xSpace.x,
      y: ySpace.y,
      width: xSpace.width,
      height: ySpace.height
    };
  } else {
    xSpace = getVertSpace(
      availSpace,
      xAxisPosition,
      yAxisPosition === 'bottom' ? yMaxDim : 0,
      yAxisPosition === 'bottom' ? 0 : yMaxDim,
      xMaxDim
    );
    ySpace = getHorizSpace(
      availSpace,
      yAxisPosition as 'top' | 'bottom',
      xAxisPosition === 'left' ? xMaxDim : 0,
      xAxisPosition === 'left' ? 0 : xMaxDim,
      yMaxDim
    );

    plotAreaSpace = {
      x: ySpace.x,
      y: xSpace.y,
      width: ySpace.width,
      height: xSpace.height
    };
  }

  return { xSpace, ySpace, plotAreaSpace };
}

/**
 * Returns the vertical sub section of given available space.
 * @param availSpace The available space.
 * @param position The position of the vertical section.
 * @param bottomOffset The bottom offset of the vertical section.
 * @param topOffset The top offset of the vertical section.
 * @param width The width of the section.
 * @returns
 */
export function getVertSpace(
  availSpace: Dimension,
  position: 'left' | 'right',
  bottomOffset: number,
  topOffset: number,
  width: number
) {
  return {
    x: position === 'left' ? availSpace.x : availSpace.x + availSpace.width - width,
    width: width,
    y: availSpace.y + topOffset,
    height: availSpace.height - bottomOffset - topOffset
  };
}

/**
 * Returns the horizontal sub section of given available space.
 * @param availSpace The available space.
 * @param position The position of the horizontal section.
 * @param leftOffset The left offset of the horizontal section.
 * @param rightOffset The right offset of the horizontal section.
 * @param height The height of the section.
 * @returns
 */
export function getHorizSpace(
  availSpace: Dimension,
  position: 'top' | 'bottom',
  leftOffset: number,
  rightOffset: number,
  height: number
) {
  return {
    x: availSpace.x + leftOffset,
    width: availSpace.width - leftOffset - rightOffset,
    y: position === 'top' ? availSpace.y : availSpace.y + availSpace.height - height,
    height: height
  };
}

/**
 * Returns the outer spacing for the chart.
 * @param width The height of the chart.
 * @param height The width of the chart.
 * @returns
 */
export function getOuterGaps(width: number, height: number) {
  const widthRatio = Math.min(width / 400, 1);
  const heightRatio = Math.min(height / 300, 1);
  return {
    width: widthRatio * OUTER_GAP_WIDTH,
    height: heightRatio * OUTER_GAP_HEIGHT
  };
}

/**
 * Returns the default available space for chart.
 * @param width The width of the chart.
 * @param height The height of the chart.
 * @returns
 */
export function getAvailSpace(width: number, height: number) {
  const gaps = getOuterGaps(width, height);
  return {
    width: width - 2 * gaps.width,
    height: height - 2 * gaps.height,
    x: gaps.width,
    y: gaps.height
  };
}

/**
 * Clones the given dimension.
 */
export function cloneDimension(dims: Dimension) {
  return {
    x: dims.x,
    y: dims.y,
    height: dims.height,
    width: dims.width
  };
}

/**
 * Retuns the available space for the scrollbar.
 * @param availSpace
 * @param zoomAndScroll
 * @param xAxisPosition
 * @returns
 */
export function getScrollBarSpace(
  availSpace: Dimension,
  zoomAndScroll: 'live' | 'off',
  xAxisPosition: AxisPosition
) {
  const dims = { x: 0, y: 0, height: 0, width: 0 };
  const avSpace = cloneDimension(availSpace);
  if (zoomAndScroll === 'off') {
    return { scrollDims: dims, availSpace: avSpace };
  }
  if (xAxisPosition === 'left') {
    dims.x = availSpace.x;
    dims.y = availSpace.y;
    dims.width = MIN_SCROLL_SIZE;
    dims.height = availSpace.height;

    avSpace.x += dims.width + SCROLL_GAP;
    avSpace.width -= dims.width + SCROLL_GAP;
  } else if (xAxisPosition === 'right') {
    dims.x = availSpace.x + availSpace.width - MIN_SCROLL_SIZE;
    dims.width = MIN_SCROLL_SIZE;
    dims.height = availSpace.height;
    dims.y = availSpace.y;

    avSpace.width -= dims.width + SCROLL_GAP;
  } else {
    dims.x = availSpace.x;
    dims.y = availSpace.y + availSpace.height - MIN_SCROLL_SIZE;
    dims.height = MIN_SCROLL_SIZE;
    dims.width = availSpace.width;

    avSpace.height -= dims.height + SCROLL_GAP;
  }
  return { scrollDims: dims, availSpace: avSpace };
}

/**
 * Updates the scrollbar space
 * @param availSpace
 * @param isHoriz
 * @param yMaxDim
 */
export function updateScrollBarSpace(availSpace: Dimension, isHoriz: boolean, yMaxDim: number) {
  const newDims = cloneDimension(availSpace);
  if (isHoriz) {
    newDims.width -= yMaxDim;
    newDims.x += yMaxDim;
  } else {
    newDims.height -= yMaxDim;
  }
  return newDims;
}

export function findNearestDataPoint<K extends string | number, D extends ChartItem<K>>(
  sereis: ChartSeries<K, D>[],
  xScale: Scale,
  yScale: Scale,
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  isStacked: boolean,
  getSeriesDataInfo: (seriesIndex: number) => { offset: number; dataWidth: number },
  getYCoord: (
    value: number,
    yScale: Scale,
    isStacked: boolean,
    isLog: boolean,
    dataItemGaps: number,
    negCummValue: number,
    posCummValue: number
  ) => number,
  isLog: boolean,
  dataItemGaps: number,
  hiddenIds?: Set<number | string>
) {
  return (x: number, y: number) => {
    const neareastGroupIndex = Math.round(x);
    const groupCenter = xScale.transform(neareastGroupIndex);
    let posCummValue = 0;
    let negCummValue = 0;

    let minDistance = Number.MAX_VALUE;
    let minSeriesIndex = 0;
    let minSeriesCenter = 0;

    let yCoord;

    for (let seriesIndex = 0; seriesIndex < sereis.length; seriesIndex++) {
      const item = getDataItem(seriesIndex, neareastGroupIndex);
      if (!item || hiddenIds?.has(item.id!) || item.value == null || (isLog && item.value <= 0)) {
        continue;
      }

      let dist = 0;

      if (!isStacked) {
        const { offset, dataWidth } = getSeriesDataInfo(seriesIndex);
        const seriesCenter = offset + groupCenter + dataWidth / 2;
        dist = Math.abs(xScale.transform(x) - seriesCenter);
        if (dist < minDistance) {
          minSeriesCenter = seriesCenter;
        }
      } else {
        const itemY = (item.value < 0 ? negCummValue : posCummValue) + item.value / 2;
        dist = Math.abs(y - itemY);
      }

      if (dist < minDistance) {
        minDistance = dist;
        minSeriesIndex = seriesIndex;
        yCoord = getYCoord(
          item.value,
          yScale,
          isStacked,
          isLog,
          dataItemGaps,
          negCummValue,
          posCummValue
        );
      }

      if (isStacked) {
        posCummValue += item.value > 0 ? item.value : 0;
        negCummValue += item.value < 0 ? item.value : 0;
      }
    }
    return {
      seriesIndex: minSeriesIndex,
      groupIndex: neareastGroupIndex,
      yCoord,
      xCoord: isStacked ? groupCenter : minSeriesCenter
    };
  };
}

export function getGroupCenterCoord<K extends string | number, D extends ChartItem<K>>(
  group: Group,
  groupIndex: number,
  xScale: Scale,
  item: D,
  timeAxisType?: 'enabled' | 'skipGaps' | 'mixedFrequency'
) {
  if (!timeAxisType) {
    return xScale.transform(groupIndex);
  } else if (timeAxisType === 'mixedFrequency') {
    return xScale.transform(new Date(item.x!).getTime());
  } else {
    return xScale.transform(new Date(group.id).getTime());
  }
}
